import { z } from "zod";
/**
 * Weather DTO for HealthOS.
 *
 * Source of truth: MET Norway (api.met.no, CC BY 4.0 / NLOD 2.0). The
 * worker fetches LocationForecast 2.0 + Sunrise 3.0, applies a small
 * deterministic rules engine, and returns this thin DTO.
 *
 * Editorial discipline:
 *  - The widget shows weather *because* it ties to a health action.
 *    `actionHeadline` is the headline (e.g. "Cool morning ahead.");
 *    `actionDetail` is the supporting line (e.g. "Layer up before
 *    your 10am walk - 8°C, dry.").
 *  - When no rule fires, `actionHeadline`/`actionDetail` are absent
 *    and the widget falls back to a quiet condition line.
 *  - Severe / decision-grade events (rain at a planned slot) are
 *    NOT inlined here - they're written to the Attention queue with
 *    a `weather.*` source and surfaced via the existing TriageQueue.
 */
/**
 * yr.no symbol code enum. Subset that maps to icons we author.
 * Full enum is ~80 codes (variants per time-of-day and precip-amount);
 * we collapse to the 8-icon family our SVG set supports.
 *
 * See https://api.met.no/weatherapi/weathericon/2.0/documentation
 */
export const WeatherIconCodeSchema = z.enum([
    "clear_day",
    "clear_night",
    "partly_cloudy_day",
    "partly_cloudy_night",
    "cloudy",
    "rain",
    "snow",
    "fog",
]);
/**
 * Time-of-day phase, derived from sunrise/sunset on the server. The
 * widget uses this to tint its surface (cream-warm at dawn, leaf at
 * day, teal at dusk, navy at night) without recomputing on the client.
 */
export const TimeOfDayPhaseSchema = z.enum(["dawn", "day", "dusk", "night"]);
export const WeatherTodaySchema = z.object({
    /** Human-readable location resolved from the member's selected place or IP. */
    locationLabel: z.string().min(1).max(100).optional(),
    /** Current temperature in Celsius, rounded to nearest int. */
    tempC: z.number(),
    /** Single-word condition for the icon row, e.g. "Mostly clear". */
    conditionLabel: z.string().min(1).max(40),
    /** Mapped icon code; widget renders the matching SVG + animation. */
    iconCode: WeatherIconCodeSchema,
    /** Server-derived time-of-day phase for the card's surface tint. */
    phase: TimeOfDayPhaseSchema,
    /** ISO timestamp of the forecast slice we used (for cache debugging). */
    observedAt: z.string().datetime({ offset: true }),
    /** ISO sunrise / sunset for today at the user's location. */
    sunrise: z.string().datetime({ offset: true }),
    sunset: z.string().datetime({ offset: true }),
    /** Relative humidity %, when the forecast slice carries it. */
    humidityPct: z.number().min(0).max(100).optional(),
    /** Sea-level pressure in hPa, when the forecast slice carries it. */
    pressureHpa: z.number().optional(),
    /** Wind speed in m/s, when the forecast slice carries it. */
    windMs: z.number().min(0).optional(),
    /**
     * Optional contextual nudge - present only when a rule fires. The
     * widget never fabricates a nudge; absent means "no health-relevant
     * tie-in today, just show the weather."
     */
    action: z
        .object({
        headline: z.string().min(1).max(80),
        detail: z.string().min(1).max(160),
        ruleId: z.string().min(1),
    })
        .optional(),
    /**
     * Current air quality (Open-Meteo, US EPA AQI). Absent when the
     * air-quality fetch fails; absence means unknown, never "clean".
     */
    airQuality: z
        .object({
        usAqi: z.number().min(0),
        band: z.enum([
            "good",
            "moderate",
            "unhealthy_sensitive",
            "unhealthy",
            "very_unhealthy",
            "hazardous",
        ]),
    })
        .optional(),
});
/**
 * Returned when the worker can't resolve the user's location (cf object
 * absent, no query params). Client should call navigator.geolocation
 * and retry with `?lat=…&lon=…`.
 */
export const WeatherNeedsLocationSchema = z.object({
    status: z.literal("needs-location"),
    reason: z.enum(["no_cf_geo", "invalid_coords"]),
});
export const WeatherResponseSchema = z.union([
    z.object({ status: z.literal("ok"), data: WeatherTodaySchema }),
    WeatherNeedsLocationSchema,
]);
//# sourceMappingURL=weather.js.map